// Pure order-status → what-the-employee-is-told mapping.
//
// No-data-is-not-good-news: an in-flight or unknown status must never read as
// success, and must never read as failure either. Money-safety: a state where the
// provider's answer is not yet known (RECONCILIATION_REQUIRED) is neutral info —
// we tell them they won't be charged twice, never "failed, try again".
import type { OrderStatus } from '../types';

export type StatusTone = 'pending' | 'info' | 'success' | 'warning' | 'neutral';

export interface StatusInfo {
  /** i18n key under `everydayServices:status.<key>` (title + message). */
  key: string;
  tone: StatusTone;
  badge: 'info' | 'warning' | 'success' | 'neutral' | 'orange';
  /** Still moving on its own — keep polling. */
  inFlight: boolean;
  /** A receipt can be downloaded (backend: COMPLETED or REFUNDED only). */
  receipt: boolean;
  /** English fallbacks (the EN locale carries the same copy). */
  title: string;
  message: string;
}

const MAP: Record<OrderStatus, Omit<StatusInfo, 'receipt'> & { receipt?: boolean }> = {
  AWAITING_PAYMENT: {
    key: 'awaitingPayment', tone: 'pending', badge: 'orange', inFlight: true,
    title: 'Approve the payment on your phone',
    message: 'We sent a mobile-money prompt to your wallet. Enter your PIN to approve it.',
  },
  PAYMENT_PROCESSING: {
    key: 'awaitingPayment', tone: 'pending', badge: 'orange', inFlight: true,
    title: 'Approve the payment on your phone',
    message: 'We sent a mobile-money prompt to your wallet. Enter your PIN to approve it.',
  },
  PAID: {
    key: 'delivering', tone: 'pending', badge: 'info', inFlight: true,
    title: 'Payment received — processing…',
    message: 'Your payment went through. The provider is delivering your purchase now.',
  },
  FULFILLMENT_PROCESSING: {
    key: 'delivering', tone: 'pending', badge: 'info', inFlight: true,
    title: 'Payment received — processing…',
    message: 'Your payment went through. The provider is delivering your purchase now.',
  },
  COMPLETED: {
    key: 'completed', tone: 'success', badge: 'success', inFlight: false, receipt: true,
    title: 'Delivered',
    message: 'Your purchase was delivered.',
  },
  RECONCILIATION_REQUIRED: {
    key: 'reconciling', tone: 'info', badge: 'info', inFlight: false,
    title: 'We’re confirming your purchase',
    message: 'We’re confirming your purchase. Please don’t pay again — you won’t be charged twice, and we won’t buy it again.',
  },
  REFUND_PENDING: {
    key: 'refundPending', tone: 'warning', badge: 'warning', inFlight: false,
    title: 'Not delivered — refund in progress',
    message: 'Not delivered — the provider is refunding you.',
  },
  REFUNDED: {
    key: 'refunded', tone: 'neutral', badge: 'neutral', inFlight: false, receipt: true,
    title: 'Refunded',
    message: 'Refunded by the provider.',
  },
  FAILED: {
    key: 'failed', tone: 'neutral', badge: 'neutral', inFlight: false,
    title: 'Payment declined',
    message: 'The payment was declined or cancelled — you were not charged.',
  },
  EXPIRED: {
    key: 'expired', tone: 'neutral', badge: 'neutral', inFlight: false,
    title: 'Payment not approved in time',
    message: 'The payment wasn’t approved in time — you were not charged.',
  },
  CANCELLED: {
    key: 'cancelled', tone: 'neutral', badge: 'neutral', inFlight: false,
    title: 'Cancelled',
    message: 'This purchase was cancelled.',
  },
};

const UNKNOWN: StatusInfo = {
  key: 'unknown', tone: 'info', badge: 'info', inFlight: false, receipt: false,
  title: 'Checking this purchase',
  message: 'We’re checking the latest status with the provider. Check back in a moment.',
};

// The payment answer is not yet known (the prompt reply was lost, or the
// provider hasn't confirmed): neither success nor failure, and never "pay again".
const CONFIRMING: StatusInfo = {
  key: 'confirmingPayment', tone: 'info', badge: 'info', inFlight: true, receipt: false,
  title: 'We’re confirming your purchase',
  message: 'We’re confirming your purchase. Please don’t pay again.',
};

export function statusInfo(status: string | null | undefined, paymentStatus?: string | null): StatusInfo {
  const s = (status || '').toUpperCase() as OrderStatus;
  if (s === 'PAYMENT_PROCESSING' && (paymentStatus || '').toUpperCase() === 'UNKNOWN') return CONFIRMING;
  const m = MAP[s];
  return m ? { receipt: false, ...m } : UNKNOWN;
}

/** No "buy again" while the outcome is uncertain — the employee might pay twice. */
export function canBuyAgain(status: string | null | undefined, paymentStatus?: string | null): boolean {
  const k = statusInfo(status, paymentStatus).key;
  return ['completed', 'failed', 'expired', 'cancelled', 'refunded'].includes(k);
}

/** Statuses the UI polls through (every ~3s, capped at ~3 minutes). */
export const POLL_INTERVAL_MS = 3000;
export const POLL_MAX_MS = 3 * 60 * 1000;

export const KNOWN_STATUSES = Object.keys(MAP) as OrderStatus[];
